/**
 * WebSocket adapters for the notifications system
 */

import { WebSocketAdapter } from '../types';
import { EventEmitter } from 'eventemitter3';

/**
 * Default WebSocket adapter using the 'ws' library
 */
export class DefaultWebSocketAdapter extends EventEmitter implements WebSocketAdapter {
  private ws: any; // WebSocket instance
  private url: string;
  private reconnectInterval: number;
  private maxReconnectAttempts: number;
  private reconnectAttempts: number = 0;
  private isConnected: boolean = false;
  private reconnectTimer?: NodeJS.Timeout;

  constructor(url: string, options: {
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
  } = {}) {
    super();
    this.url = url;
    this.reconnectInterval = options.reconnectInterval || 5000;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
  }

  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Dynamic import to avoid bundling issues
        import('ws').then(({ WebSocket }) => {
          this.ws = new WebSocket(this.url);

          this.ws.on('open', () => {
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.emit('connected');
            resolve();
          });

          this.ws.on('message', (data: Buffer) => {
            try {
              const message = JSON.parse(data.toString());
              this.emit('message', message);
              
              // Emit specific events based on message type
              if (message.event) {
                this.emit(message.event, message.data);
              }
            } catch (error) {
              this.emit('error', error);
            }
          });

          this.ws.on('close', () => {
            this.isConnected = false;
            this.emit('disconnected');
            this.handleReconnect();
          });

          this.ws.on('error', (error: Error) => {
            this.emit('error', error);
            reject(error);
          });
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('reconnectFailed');
      return;
    }

    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.connect().catch(() => {
        // Reconnect failed, will be handled by handleReconnect
      });
    }, this.reconnectInterval);
  }

  async broadcastToRecipient(recipientId: string | number, notification: any): Promise<void> {
    if (!this.isConnected || !this.ws) {
      throw new Error('WebSocket not connected');
    }

    const message = {
      type: 'notification',
      event: 'notification:created',
      recipientId,
      data: notification,
      timestamp: new Date().toISOString(),
    };

    this.ws.send(JSON.stringify(message));
  }

  async broadcast(notification: any): Promise<void> {
    if (!this.isConnected || !this.ws) {
      throw new Error('WebSocket not connected');
    }

    const message = {
      type: 'broadcast',
      event: 'notification:created',
      data: notification,
      timestamp: new Date().toISOString(),
    };

    this.ws.send(JSON.stringify(message));
  }

  close(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    
    if (this.ws) {
      this.ws.close();
    }
    
    this.isConnected = false;
  }

  get connected(): boolean {
    return this.isConnected;
  }
}

/**
 * Socket.IO adapter (to be implemented by developers)
 * This is a template showing how to integrate with Socket.IO
 */
export class SocketIOAdapter extends EventEmitter implements WebSocketAdapter {
  private socket: any; // Socket.IO client instance
  private isConnected: boolean = false;

  constructor(socket: any) {
    super();
    this.socket = socket;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.emit('connected');
    });

    this.socket.on('disconnect', () => {
      this.isConnected = false;
      this.emit('disconnected');
    });

    this.socket.on('notification', (data: any) => {
      this.emit('notification', data);
    });

    this.socket.on('error', (error: any) => {
      this.emit('error', error);
    });
  }

  async broadcastToRecipient(recipientId: string | number, notification: any): Promise<void> {
    if (!this.isConnected) {
      throw new Error('Socket.IO not connected');
    }

    this.socket.emit('notification', {
      event: 'notification:created',
      recipientId,
      data: notification,
      timestamp: new Date().toISOString(),
    });
  }

  async broadcast(notification: any): Promise<void> {
    if (!this.isConnected) {
      throw new Error('Socket.IO not connected');
    }

    this.socket.emit('broadcast', {
      event: 'notification:created',
      data: notification,
      timestamp: new Date().toISOString(),
    });
  }

  close(): void {
    if (this.socket) {
      this.socket.disconnect();
    }
    this.isConnected = false;
  }

  get connected(): boolean {
    return this.isConnected;
  }
}

/**
 * Pusher adapter (to be implemented by developers)
 * This is a template showing how to integrate with Pusher
 */
export class PusherAdapter extends EventEmitter implements WebSocketAdapter {
  private pusher: any; // Pusher client instance
  private channel: any;

  constructor(pusher: any, channelName: string = 'notifications') {
    super();
    this.pusher = pusher;
    this.channel = pusher.subscribe(channelName);
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.channel.bind('notification', (data: any) => {
      this.emit('notification', data);
    });

    this.channel.bind('error', (error: any) => {
      this.emit('error', error);
    });
  }

  async broadcastToRecipient(recipientId: string | number, notification: any): Promise<void> {
    // Pusher doesn't support direct recipient targeting in the client
    // This would typically be handled on the server side
    throw new Error('PusherAdapter: broadcastToRecipient not supported on client side');
  }

  async broadcast(notification: any): Promise<void> {
    // Pusher doesn't support broadcasting from the client
    // This would typically be handled on the server side
    throw new Error('PusherAdapter: broadcast not supported on client side');
  }

  close(): void {
    if (this.pusher) {
      this.pusher.disconnect();
    }
  }

  get connected(): boolean {
    return this.pusher.connection.state === 'connected';
  }
}

/**
 * Mock WebSocket adapter for testing
 */
export class MockWebSocketAdapter extends EventEmitter implements WebSocketAdapter {
  private isConnected: boolean = false;

  async connect(): Promise<void> {
    this.isConnected = true;
    this.emit('connected');
  }

  async broadcastToRecipient(recipientId: string | number, notification: any): Promise<void> {
    // Simulate sending to recipient
    setTimeout(() => {
      this.emit('notification', {
        event: 'notification:created',
        recipientId,
        data: notification,
        timestamp: new Date().toISOString(),
      });
    }, 10);
  }

  async broadcast(notification: any): Promise<void> {
    // Simulate broadcasting
    setTimeout(() => {
      this.emit('broadcast', {
        event: 'notification:created',
        data: notification,
        timestamp: new Date().toISOString(),
      });
    }, 10);
  }

  close(): void {
    this.isConnected = false;
    this.emit('disconnected');
  }

  get connected(): boolean {
    return this.isConnected;
  }
}
